import { existsSync } from "node:fs";
import { readFile } from "node:fs/promises";
import { basename, join } from "node:path";
import fg from "fast-glob";
import { optimize } from "svgo";
import type { IconData } from "../types";
import { kebabToPascalCase } from "./naming";

/**
 * Get all SVG files from the icons directory
 */
export async function getAllIcons(svgDir: string): Promise<string[]> {
  const pattern = join(svgDir, "*.svg");
  const files = await fg(pattern, { onlyFiles: true });

  return files
    .map((file) => basename(file, ".svg"))
    .sort((a, b) => a.localeCompare(b));
}

/**
 * Parse SVG file and extract required data
 */
export async function parseSvg(
  svgPath: string,
  iconName: string,
): Promise<IconData> {
  const svgContentRaw = await readFile(svgPath, "utf-8");

  const result = optimize(svgContentRaw, {
    path: svgPath,
    plugins: ["preset-default"],
  });

  const svgContent = result.data;

  // Extract viewBox
  const viewBoxMatch = /viewBox="([^"]+)"/.exec(svgContent);
  const viewBox = viewBoxMatch?.[1] ?? "0 0 24 24";

  // Validate viewBox
  if (viewBox !== "0 0 24 24") {
    console.warn(
      `Warning: Non-standard viewBox detected in ${iconName}: ${viewBox}`,
    );
  }

  // Extract path elements
  const pathMatches = svgContent.match(/<path[^>]*\/>/g);
  const paths = pathMatches ? pathMatches.join("\n      ") : "";

  if (!paths) {
    throw new Error(`No path elements found in ${iconName}.svg`);
  }

  return {
    name: iconName,
    componentName: kebabToPascalCase(iconName),
    svgContent,
    viewBox,
    paths,
  };
}

/**
 * Check if SVG file exists
 */
export async function iconExists(
  svgDir: string,
  iconName: string,
): Promise<boolean> {
  const svgPath = join(svgDir, `${iconName}.svg`);
  return existsSync(svgPath);
}

/**
 * Get path to SVG file
 */
export function getSvgPath(svgDir: string, iconName: string): string {
  return join(svgDir, `${iconName}.svg`);
}
